import { useEffect } from 'react';
import { decideSurface } from '../lib/hostPolicy';
import ClientHome from '../pages/ClientHome';

/**
 * Applies the host policy ONCE, at boot, above every provider and the router.
 *
 * ⭐ WHY BOOT TIME IS ENOUGH. A gated client host only ever renders client pages, and those carry
 * no staff navigation - there is no control on `/portal/:token` that routes to `/dashboard`. So
 * the only way a staff path is reached on a client host is by typing or following a link, which is
 * a fresh document load and runs this again. Re-deciding on every client-side navigation would
 * couple this to the router for a case the UI cannot produce.
 *
 * ⛔ IT SITS OUTSIDE `AuthProvider` DELIBERATELY. A handoff must not first mount auth and fire a
 * session check against an origin the visitor is about to leave.
 *
 * ⚠️ `window.location.replace`, never `assign`: a client who lands here by trimming a URL should
 * not be able to press Back into a redirect loop.
 */
export default function HostGate({ children }: { children: React.ReactNode }) {
  const { hostname, pathname, search, hash } = window.location;
  const decision = decideSurface(hostname, pathname, search, hash);
  const handoffTo = decision.action === 'handoff' ? decision.to : null;

  useEffect(() => {
    if (handoffTo) window.location.replace(handoffTo);
    // The decision is derived from window.location, which cannot change without a document load,
    // so this runs once. Keyed on the resolved target rather than the decision object so a
    // re-render with an equal decision does not re-fire the redirect.
  }, [handoffTo]);

  if (decision.action === 'handoff') return null;
  if (decision.action === 'client-home') return <ClientHome />;
  return <>{children}</>;
}
